import React, { Fragment, useState } from 'react';
import { Tabs, Row, Col, Table, Button, Divider } from 'antd';
import {
  FileTextOutlined,
  FileSearchOutlined,
  HistoryOutlined,
  DownloadOutlined,
  FileProtectOutlined,
} from '@ant-design/icons';
import {
  CustomButton,
  CustomModal,
  CustomTable,
  CustomText,
  FormInput,
} from '../../../../components';
import { Item, TransfersRequest } from '../../types/transfers-response';
import ManageStatusRequest from '../components/ByRoles/ManageStatusRequest';
import { ShareResponse } from '../../../../share';
import ManageAllAsset from '../components/ByRoles/ManageAllAsset';
import ManageOneAsset from '../components/ByRoles/ManageOneAsset';
import AdditionalAssets from '../components/AdditionalAssets';

interface TransfersDetailViewProps {
  detailData?: TransfersRequest;
  setFooterLeft: (footer: React.ReactNode) => void;
  onVoucher?: (id: string) => void;
  onDocuments: (id: string) => void;
  goodStates?: ShareResponse[];
  formik: any;
  manageItemsTransfer: (data: any) => Promise<void>;
  isApproveRequest?: boolean;
  dataItems?: Item[];
  isLoadingItems: boolean;
}

const TransfersDetailView: React.FC<TransfersDetailViewProps> = ({
  detailData,
  setFooterLeft,
  onDocuments,
  onVoucher,
  goodStates,
  formik,
  manageItemsTransfer,
  isApproveRequest,
  dataItems,
  isLoadingItems,
}) => {
  const [filteredData, setFilteredData] = useState(dataItems);
  const [searchText, setSearchText] = useState('');
  const [modalAllAsset, setModalAllAsset] = useState<boolean>(false);
  const [modalOneAsset, setModalOneAsset] = useState<boolean>(false);
  const [item, setItem] = useState<any>([]);
  const [modalOpen, setModalOpen] = React.useState(false);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value || '';
    setSearchText(value);

    if (!value) {
      setFilteredData(dataItems || []);
      return;
    }

    const valueLower = value.toLowerCase();

    const filtered = (dataItems || []).filter((item) => {
      return (
        item.plate_code?.toLowerCase().includes(valueLower) ||
        String(item.id)?.toLowerCase().includes(valueLower)
      );
    });

    setFilteredData(filtered);
  };

  React.useEffect(() => {
    if (modalOneAsset) {
      formik.resetForm();
    }
  }, [modalOneAsset]);

  React.useEffect(() => {
    if (modalAllAsset) {
      formik.resetForm();
    }
  }, [modalAllAsset]);

  React.useEffect(() => {
    if (!dataItems) {
      setFilteredData([]);
      return;
    }

    setFilteredData(dataItems);
  }, [dataItems]);

  // FOOTER
  React.useEffect(() => {
    setFooterLeft(
      <Fragment>
        <CustomButton
          disabled={Number(detailData?.status_id) !== 7}
          label={'Generar comprobante'}
          onClick={() => onVoucher?.(String(detailData?.id_petition))}
          icon={<FileProtectOutlined />}
          size="middle"
          noBorder
          buttonType={'secondary'}
        />
        <CustomButton
          label={'Descargar documentos'}
          onClick={() => onDocuments(String(detailData?.id))}
          icon={<DownloadOutlined />}
          size="middle"
          buttonType={'secondary'}
          disabled={!detailData?.documents || detailData.documents.length === 0}
        />
      </Fragment>
    );
  }, [setFooterLeft, detailData?.id]);

  const onSaveManageAllAsset = () => {
    const data = {
      ids: dataItems?.map((item) => item?.id),
      status_id: formik.values.status_id,
      observation: formik.values.reason,
    };
    manageItemsTransfer(data).finally(() => setModalAllAsset(false));
  };

  const onSaveManageOneAsset = () => {
    const [value] = item;

    const data = {
      ids: [value?.id],
      status_id: formik.values.status_id,
      observation: formik.values.reason,
    };

    manageItemsTransfer(data).finally(() => {
      setModalOneAsset(false);
      setItem([]);
    });
  };

  return (
    <Fragment>
      <CustomModal
        title={`Ver adiciones`}
        labelCancel={`Finalizar`}
        open={modalOpen}
        width="95vw"
        onCancel={() => setModalOpen(false)}
      >
        <AdditionalAssets items={item} />
      </CustomModal>
      <CustomModal
        title=""
        labelSubmit="Aceptar"
        open={modalAllAsset}
        width="95vw"
        onCancel={() => setModalAllAsset(false)}
        onSave={onSaveManageAllAsset}
        disabledBtn={
          formik.values.status_id && formik.values.reason ? false : true
        }
      >
        <ManageAllAsset
          formik={formik}
          detailData={detailData}
          goodPetitionItems={goodStates}
        />
      </CustomModal>

      <CustomModal
        title={`Detalle Traslado de funcionarios ${detailData?.ticket || ''}`}
        labelSubmit="Aceptar"
        open={modalOneAsset}
        width="95vw"
        onCancel={() => setModalOneAsset(false)}
        onSave={onSaveManageOneAsset}
        disabledBtn={
          formik.values.status_id && formik.values.reason ? false : true
        }
      >
        <ManageOneAsset items={item} formik={formik} />
      </CustomModal>
      {!!(detailData?.can_manage && isApproveRequest) && (
        <ManageStatusRequest
          petitionStates={goodStates}
          detailData={detailData}
          formikSolicitud={formik}
        />
      )}
      <Tabs
        defaultActiveKey="1"
        items={[
          {
            key: '1',
            label: (
              <Button type="link" icon={<FileTextOutlined />}>
                Información general
              </Button>
            ),
            children: (
              <>
                <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
                  <Col span={6}>
                    <div className="mb-4">
                      <CustomText text="Solicitante:" strong fontSize="small" />
                      <CustomText
                        text={detailData?.requester || 'Por definir'}
                        fontSize="middle"
                      />
                    </div>
                  </Col>
                  <Col span={6}>
                    <div className="mb-4">
                      <CustomText text="Dependencia:" strong fontSize="small" />
                      <CustomText
                        text={detailData?.dependency || 'Por definir'}
                        fontSize="middle"
                      />
                    </div>
                  </Col>
                  <Col span={6}>
                    <div className="mb-4">
                      <CustomText
                        text="Nuevo responsable:"
                        strong
                        fontSize="small"
                      />
                      <CustomText
                        text={detailData?.new_responsible || 'Por definir'}
                        fontSize="middle"
                      />
                    </div>
                  </Col>
                  <Col span={6}>
                    <div className="mb-4">
                      <CustomText text="Dependencia nuevo responsable:" strong fontSize="small" />
                      <CustomText
                        text={detailData?.new_dependency || 'Por definir'}
                        fontSize="middle"
                      />
                    </div>
                  </Col>
                </Row>
                <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
                  <Col span={6}>
                    <div className="mb-4">
                      <CustomText
                        text="Fecha solicitud:"
                        strong
                        fontSize="small"
                      />
                      <CustomText
                        text={detailData?.query_date || 'Por definir'}
                        fontSize="middle"
                      />
                    </div>
                  </Col>
                  <Col span={6}>
                    <div className="mb-4">
                      <CustomText
                        text="Estado de solicitud:"
                        strong
                        fontSize="small"
                      />
                      <CustomText
                        text={detailData?.status || 'Por definir'}
                        fontSize="middle"
                      />
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="mb-4">
                      <CustomText
                        text="Observaciones:"
                        strong
                        fontSize="small"
                      />
                      <CustomText
                        text={detailData?.observations || 'Por definir'}
                        fontSize="middle"
                      />
                    </div>
                  </Col>
                </Row>
                <Row
                  justify="space-between"
                  align="middle"
                  style={{ marginBottom: '20px' }}
                >
                  <Col xs={24} sm={12}>
                    <CustomText
                      text="Listado de activos"
                      strong
                      fontSize="big"
                    />
                  </Col>

                  <Col xs={24} sm={12} style={{ textAlign: 'right' }}>
                    <FormInput
                      placeholder="Buscar..."
                      value={searchText}
                      onChange={handleSearch}
                      label="Buscador"
                      name="search"
                    />
                  </Col>
                </Row>
                {!!(detailData?.can_manage && isApproveRequest) && (
                  <Row className="mb-4" gutter={[16, 16]} justify="end">
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                      lg={8}
                      span={12}
                      style={{ textAlign: 'right' }}
                    >
                      <Button onClick={() => setModalAllAsset(true)}>
                        Gestionar todos
                      </Button>
                    </Col>
                  </Row>
                )}
                <Row gutter={[16, 16]} justify="end">
                  <Col xs={24} sm={24} md={24} lg={24}>
                    <CustomTable<Item>
                      loading={isLoadingItems}
                      rowKey="id"
                      scroll={{ x: 'max-content' }}
                      columns={[
                        { title: 'Item', dataIndex: 'id', key: 'id' },
                        {
                          title: 'Código/placa',
                          dataIndex: 'plate_code',
                          key: 'plate_code',
                        },
                        {
                          title: 'Descripción',
                          dataIndex: 'asset',
                          key: 'asset',
                        },
                        {
                          title: 'Clase de activo',
                          dataIndex: 'class',
                          key: 'class',
                          render: (_: any, record: any) =>
                            `${record.class_code} - ${record.class}`,
                        },
                        {
                          title: 'Artículo',
                          key: 'item',
                          render: (_: any, record: any) =>
                            `${record.item_code} - ${record.item}`,
                        },
                        {
                          title: 'Adiciones',
                          dataIndex: 'assets_count',
                          key: 'assets_count',
                        },
                        {
                          title: 'Estado del elemento',
                          dataIndex: 'status',
                          key: 'status',
                        },
                        {
                          title: 'Observaciones',
                          dataIndex: 'observations',
                          key: 'observations',
                          render: (text: string) => (
                            <div
                              style={{
                                maxWidth: 220,
                                whiteSpace: 'pre-wrap',
                                wordWrap: 'break-word',
                              }}
                            >
                              {text || 'Sin registro'}
                            </div>
                          ),
                        },
                        {
                          title: 'Acciones',
                          key: 'actions',
                          render: (value) => (
                            <Button
                              disabled={!Number(value.assets_count)}
                              onClick={() => {
                                setItem([{ ...value }]);
                                setModalOpen(true);
                              }}
                            >
                              Ver adiciones
                            </Button>
                          ),
                        },
                      ]}
                      dataSource={filteredData}
                    />
                  </Col>
                </Row>
              </>
            ),
          },
          {
            key: '2',
            label: (
              <Button type="link" icon={<FileSearchOutlined />}>
                Documentos
              </Button>
            ),
            children: (
              <>
                <Table
                  columns={[
                    {
                      title: 'Ítem',
                      dataIndex: 'index',
                      key: 'index',
                      render: (_, __, index: number) => index + 1,
                    },
                    {
                      title: 'Nombre',
                      dataIndex: 'name',
                      key: 'name',
                    },
                  ]}
                  dataSource={detailData?.documents?.map((doc, index) => ({
                    key: index,
                    createdAt: doc.created_at,
                    name: doc.url?.split('/').pop(),
                    link: doc.url,
                  }))}
                />
                <Divider />
              </>
            ),
          },
          {
            key: '3',
            label: (
              <Button type="link" icon={<HistoryOutlined />}>
                Historial de la solicitud
              </Button>
            ),
            children: (
              <CustomTable
                columns={[
                  {
                    title: 'Item',
                    dataIndex: 'index',
                    key: 'index',
                    render: (_, __, index: number) => index + 1,
                  },
                  {
                    title: 'Fecha',
                    dataIndex: 'date',
                    key: 'date',
                  },
                  {
                    title: 'Realizado por',
                    dataIndex: 'user',
                    key: 'user',
                  },
                  {
                    title: 'Observaciones',
                    dataIndex: 'observations',
                    key: 'observations',
                  },
                  {
                    title: 'Estado',
                    dataIndex: 'comment',
                    key: 'comment',
                  },
                ]}
                dataSource={detailData?.history?.map((item, index) => ({
                  key: index,
                  date: item.date,
                  comment: item.comment,
                  observations: item.observations,
                  user: item.user_document + '' + ' - ' + item.user,
                }))}
                pagination={false}
              />
            ),
          },
        ]}
      />
    </Fragment>
  );
};

export default TransfersDetailView;
